import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
  Plus, Trash2, Pencil, ChevronDown, Camera, X, MessageSquarePlus,
  Sparkles, Download, RefreshCw, BookOpen, TrendingUp, TrendingDown,
  FileText,
} from "lucide-react";
import api, { API_BASE } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { RESOURCES } from "@/constants/testIds";
import { EmotionPicker, EmotionRowBadges } from "@/components/EmotionPicker";
import { EMOTIONS } from "@/lib/emotions";

const STAT_TONES = {
  slate: "text-slate-900",
  green: "text-emerald-600",
  red: "text-rose-600",
  amber: "text-amber-600",
};

function StatChip({ label, value, tone = "slate" }) {
  const { t } = useTranslation();
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4">
      <div className="text-[10px] uppercase tracking-widest font-semibold text-slate-500">{t(label)}</div>
      <div className={`mt-1 font-display font-bold text-2xl ${STAT_TONES[tone]}`}>{value}</div>
    </div>
  );
}

function PlaybookPicker({ form, set }) {
  const { t } = useTranslation();
  const [books, setBooks] = useState([]);
  useEffect(() => {
    api.get("/playbooks").then((r) => setBooks([...r.data.mine, ...r.data.templates].filter((p) => !p.locked))).catch(() => {});
  }, []);
  if (!books.length) return null;
  return (
    <div className="col-span-2 sm:col-span-4 grid sm:grid-cols-[1fr_auto] gap-3 items-end">
      <div>
        <label className="text-[10px] uppercase tracking-widest font-semibold text-slate-500">{t("playbooks.journalPlaybook")}</label>
        <select data-testid="journal-playbook-select" value={form.playbook_id} onChange={(e) => set("playbook_id", e.target.value)}
          className="mt-1 h-10 w-full rounded-md border border-slate-300 bg-white px-3 text-sm">
          <option value="">{t("tradeJournalUi.noneOption")}</option>
          {books.map((p) => <option key={p.id} value={p.id}>{p.title}</option>)}
        </select>
      </div>
      {form.playbook_id && (
        <label className="inline-flex items-center gap-2 h-10 text-sm text-slate-700">
          <input type="checkbox" data-testid="journal-checklist-followed" checked={form.checklist_followed} onChange={(e) => set("checklist_followed", e.target.checked)} />
          {t("playbooks.journalFollowed")}
        </label>
      )}
    </div>
  );
}

const emptyEntry = {
  instrument: "",
  direction: "long",
  entry: "",
  stop: "",
  exit: "",
  size: "",
  traded_on: "",
  entry_time: "",
  notes: "",
  account_id: "",
  climb_plan_id: "",
  playbook_id: "",
  checklist_followed: false,
  emotion_entry: null,
  emotion_management: null,
  emotion_exit: null,
};

function toFormShape(row) {
  if (!row) return { ...emptyEntry, traded_on: new Date().toLocaleDateString("en-CA") };
  return {
    instrument: row.instrument || "",
    direction: row.direction || "long",
    entry: row.entry ?? "",
    stop: row.stop ?? "",
    exit: row.exit ?? "",
    size: row.size ?? "",
    traded_on: row.traded_on || "",
    entry_time: row.entry_time || "",
    notes: row.notes || "",
    account_id: row.account_id || "",
    climb_plan_id: row.climb_plan_id || "",
    playbook_id: row.playbook_id || "",
    checklist_followed: !!row.checklist_followed,
    emotion_entry: row.emotion_entry || null,
    emotion_management: row.emotion_management || null,
    emotion_exit: row.emotion_exit || null,
  };
}

// Auto-compute P&L and R multiple from entry / stop / exit / size / direction.
// Returns { pnl, r_multiple, dollarRisk } — any may be null if inputs incomplete.
function computeDerived(form) {
  const entry = parseFloat(form.entry);
  const exit = parseFloat(form.exit);
  const stop = parseFloat(form.stop);
  const size = parseFloat(form.size);
  const dir = form.direction === "short" ? -1 : 1;

  let pnl = null;
  if (Number.isFinite(entry) && Number.isFinite(exit) && Number.isFinite(size) && size > 0) {
    pnl = Number(((exit - entry) * dir * size).toFixed(2));
  }

  let r_multiple = null;
  let dollarRisk = null;
  if (Number.isFinite(entry) && Number.isFinite(stop) && Number.isFinite(size) && size > 0) {
    const perUnit = Math.abs(entry - stop);
    if (perUnit > 0) {
      dollarRisk = Number((perUnit * size).toFixed(2));
      if (pnl !== null && dollarRisk > 0) {
        r_multiple = Number((pnl / dollarRisk).toFixed(2));
      }
    }
  }

  return { pnl, r_multiple, dollarRisk };
}

/* -------------------------------------------------------------------------- */
/*  Trade form (create + edit)                                                */
/* -------------------------------------------------------------------------- */

function TradeForm({ initial, onCancel, onSaved, accounts, plans }) {
  const { t } = useTranslation();
  const isEdit = Boolean(initial);
  const [form, setForm] = useState(() => toFormShape(initial));
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [images, setImages] = useState(initial?.images || []);
  const [savedId, setSavedId] = useState(initial?.id || null);
  const fileInputRef = useRef(null);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const derived = computeDerived(form);

  // Restrict Climb plan dropdown to plans that match the selected account
  // (if any) — plans without an account link are always shown.
  const filteredPlans = useMemo(() => {
    if (!form.account_id) return plans;
    return plans.filter((p) => !p.account_id || p.account_id === form.account_id);
  }, [plans, form.account_id]);

  const buildPayload = () => {
    const d = computeDerived(form);
    return {
      instrument: form.instrument.trim(),
      direction: form.direction,
      entry: parseFloat(form.entry),
      stop: form.stop === "" ? null : parseFloat(form.stop),
      exit: form.exit === "" ? null : parseFloat(form.exit),
      size: form.size === "" ? null : parseFloat(form.size),
      pnl: d.pnl,
      r_multiple: d.r_multiple,
      notes: form.notes,
      traded_on: form.traded_on || undefined,
      entry_time: form.entry_time || null,
      entry_tz: form.entry_time ? Intl.DateTimeFormat().resolvedOptions().timeZone : null,
      emotion_entry: form.emotion_entry || null,
      emotion_management: form.emotion_management || null,
      emotion_exit: form.emotion_exit || null,
      account_id: form.account_id || null,
      climb_plan_id: form.climb_plan_id || null,
      playbook_id: form.playbook_id || null,
      checklist_followed: !!(form.playbook_id && form.checklist_followed),
    };
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.instrument.trim() || !form.entry) {
      toast.error(t("tradeJournalUi.instrumentAndEntryRequired"));
      return;
    }
    setSaving(true);
    try {
      const payload = buildPayload();
      const res = isEdit
        ? await api.patch(`/journal/entries/${initial.id}`, payload)
        : await api.post("/journal/entries", payload);
      toast.success(isEdit ? t("tradeJournalUi.tradeUpdated") : t("tradeJournalUi.tradeLogged"));
      setSavedId(res.data.id);
      setImages(res.data.images || []);
      onSaved(res.data);
    } catch {
      toast.error(t("tradeJournalUi.couldNotSaveTrade"));
    } finally {
      setSaving(false);
    }
  };

  const uploadImage = async (file) => {
    // Auto-save the trade first if it isn't saved yet
    let id = savedId;
    if (!id) {
      if (!form.instrument.trim() || !form.entry) {
        toast.error(t("tradeJournalUi.saveTradeFirst"));
        return;
      }
      try {
        const res = await api.post("/journal/entries", buildPayload());
        id = res.data.id;
        setSavedId(id);
      } catch {
        toast.error(t("tradeJournalUi.couldNotSaveBeforeUpload"));
        return;
      }
    }
    setUploading(true);
    try {
      const body = new FormData();
      body.append("file", file);
      const res = await api.post(`/journal/entries/${id}/images`, body, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setImages(res.data.images || []);
      onSaved(res.data);
      toast.success(t("tradeJournalUi.imageUploaded"));
    } catch (err) {
      const msg = err?.response?.data?.detail || t("tradeJournalUi.uploadFailed");
      toast.error(msg);
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const removeImage = async (imgId) => {
    if (!savedId) return;
    try {
      const res = await api.delete(`/journal/entries/${savedId}/images/${imgId}`);
      setImages(res.data.images || []);
      onSaved(res.data);
    } catch {
      toast.error(t("tradeJournalUi.couldNotRemoveImage"));
    }
  };

  return (
    <form onSubmit={submit} className="mb-6 grid grid-cols-2 sm:grid-cols-4 gap-3 p-4 rounded-xl bg-slate-50 border border-slate-200">
      <div className="col-span-2 sm:col-span-4 flex items-center justify-between">
        <div className="text-sm font-semibold text-slate-700">
          {isEdit ? t("tradeJournalUi.editTrade") : t("tradeJournalUi.logNewTrade")}
        </div>
        <button type="button" onClick={onCancel} className="text-slate-400 hover:text-slate-700">
          <X size={16} />
        </button>
      </div>

      <div className="col-span-2 sm:col-span-2">
        <label className="text-[10px] uppercase tracking-widest font-semibold text-slate-500">{t("tradeJournalUi.instrument")}</label>
        <Input data-testid={RESOURCES.journalInstrument} value={form.instrument} onChange={(e) => set("instrument", e.target.value)} placeholder={t("tradeJournalUi.instrumentPlaceholder")} className="mt-1 h-10 bg-white" />
      </div>
      <div>
        <label className="text-[10px] uppercase tracking-widest font-semibold text-slate-500">{t("tradeJournalUi.direction")}</label>
        <select
          data-testid={RESOURCES.journalDirection}
          value={form.direction}
          onChange={(e) => set("direction", e.target.value)}
          className="mt-1 h-10 w-full rounded-md border border-slate-300 bg-white px-3 text-sm"
        >
          <option value="long">Long</option>
          <option value="short">Short</option>
        </select>
      </div>
      <div>
        <label className="text-[10px] uppercase tracking-widest font-semibold text-slate-500">{t("tradeJournalUi.size")}</label>
        <Input data-testid={RESOURCES.journalSize} type="number" step="any" value={form.size} onChange={(e) => set("size", e.target.value)} className="mt-1 h-10 bg-white" />
      </div>
      <div>
        <label className="text-[10px] uppercase tracking-widest font-semibold text-slate-500">{t("insights.tradeDate")}</label>
        <Input data-testid="resources-journal-traded-on" type="date" value={form.traded_on} onChange={(e) => set("traded_on", e.target.value)} className="mt-1 h-10 bg-white" />
      </div>
      <div>
        <label className="text-[10px] uppercase tracking-widest font-semibold text-slate-500">{t("insights.entryTime")}</label>
        <Input data-testid="resources-journal-entry-time" type="time" value={form.entry_time} onChange={(e) => set("entry_time", e.target.value)} className="mt-1 h-10 bg-white" />
      </div>

      <div>
        <label className="text-[10px] uppercase tracking-widest font-semibold text-slate-500">{t("tradeJournalUi.entry")}</label>
        <Input data-testid={RESOURCES.journalEntry} type="number" step="any" value={form.entry} onChange={(e) => set("entry", e.target.value)} className="mt-1 h-10 bg-white" />
      </div>
      <div>
        <label className="text-[10px] uppercase tracking-widest font-semibold text-slate-500">{t("tradeJournalUi.stop")}</label>
        <Input data-testid={RESOURCES.journalStop} type="number" step="any" value={form.stop} onChange={(e) => set("stop", e.target.value)} className="mt-1 h-10 bg-white" placeholder={t("tradeJournalUi.stopPlaceholder")} />
      </div>
      <div className="col-span-2 sm:col-span-2">
        <label className="text-[10px] uppercase tracking-widest font-semibold text-slate-500">{t("tradeJournalUi.exit")}</label>
        <Input data-testid={RESOURCES.journalExit} type="number" step="any" value={form.exit} onChange={(e) => set("exit", e.target.value)} className="mt-1 h-10 bg-white" placeholder={t("tradeJournalUi.exitPlaceholder")} />
      </div>

      {/* Live-computed P&L and R */}
      <div
        data-testid={RESOURCES.journalComputed}
        className="col-span-2 sm:col-span-4 grid grid-cols-3 gap-3 p-3 rounded-lg bg-white border border-slate-200"
      >
        <div>
          <div className="text-[10px] uppercase tracking-widest font-semibold text-slate-500">{t("tradeJournalUi.pnlAuto")}</div>
          <div
            className={`mt-1 font-mono font-bold text-lg ${
              derived.pnl == null ? "text-slate-300" : derived.pnl >= 0 ? "text-emerald-600" : "text-rose-600"
            }`}
          >
            {derived.pnl == null ? "—" : `${derived.pnl >= 0 ? "+" : ""}$${derived.pnl.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
          </div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-widest font-semibold text-slate-500">{t("tradeJournalUi.rMultipleAuto")}</div>
          <div
            className={`mt-1 font-mono font-bold text-lg ${
              derived.r_multiple == null ? "text-slate-300" : derived.r_multiple >= 0 ? "text-emerald-600" : "text-rose-600"
            }`}
          >
            {derived.r_multiple == null ? "—" : `${derived.r_multiple >= 0 ? "+" : ""}${derived.r_multiple}R`}
          </div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-widest font-semibold text-slate-500">{t("tradeJournalUi.riskOnTrade")}</div>
          <div className={`mt-1 font-mono font-bold text-lg ${derived.dollarRisk == null ? "text-slate-300" : "text-slate-900"}`}>
            {derived.dollarRisk == null ? "—" : `$${derived.dollarRisk.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
          </div>
        </div>
        <p className="col-span-3 text-[10px] text-slate-500 leading-relaxed">
          {t("tradeJournalUi.computedExplanation")}
        </p>
      </div>

      <div className="col-span-2 sm:col-span-2">
        <label className="text-[10px] uppercase tracking-widest font-semibold text-slate-500">{t("tradeJournalUi.account")}</label>
        <select
          data-testid={RESOURCES.journalAccount}
          value={form.account_id}
          onChange={(e) => {
            set("account_id", e.target.value);
            // Clear plan if it doesn't belong to the newly-selected account.
            const selectedPlan = plans.find((p) => p.id === form.climb_plan_id);
            if (selectedPlan && selectedPlan.account_id && selectedPlan.account_id !== e.target.value) {
              set("climb_plan_id", "");
            }
          }}
          className="mt-1 h-10 w-full rounded-md border border-slate-300 bg-white px-3 text-sm focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:outline-none"
        >
          <option value="">{t("tradeJournalUi.noneOption")}</option>
          {accounts.map((a) => (
            <option key={a.id} value={a.id}>
              {a.nickname}{a.account_number ? ` · #${a.account_number}` : ""}
            </option>
          ))}
        </select>
      </div>

      <div className="col-span-2 sm:col-span-2">
        <label className="text-[10px] uppercase tracking-widest font-semibold text-slate-500">
          {t("tradeJournalUi.climbPlan")}
          {form.climb_plan_id && (
            <span className="ml-2 normal-case text-[10px] text-amber-600 font-semibold">
              {t("tradeJournalUi.pnlWillUpdatePlan")}
            </span>
          )}
        </label>
        <select
          data-testid={RESOURCES.journalClimbPlan}
          value={form.climb_plan_id}
          onChange={(e) => set("climb_plan_id", e.target.value)}
          className="mt-1 h-10 w-full rounded-md border border-slate-300 bg-white px-3 text-sm focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:outline-none"
        >
          <option value="">{t("tradeJournalUi.noneOption")}</option>
          {filteredPlans.map((p) => (
            <option key={p.id} value={p.id}>{p.name}</option>
          ))}
        </select>
      </div>

      <PlaybookPicker form={form} set={set} />

      <div className="col-span-2 sm:col-span-4">
        <label className="text-[10px] uppercase tracking-widest font-semibold text-slate-500">{t("tradeJournalUi.notes")}</label>
        <Textarea data-testid={RESOURCES.journalNotes} value={form.notes} onChange={(e) => set("notes", e.target.value)} rows={2} className="mt-1 bg-white" placeholder={t("tradeJournalUi.notesPlaceholder")} />
      </div>

      <EmotionPicker value={form} onChange={setForm} />

      {/* Images */}
      <div className="col-span-2 sm:col-span-4">
        <div className="text-[10px] uppercase tracking-widest font-semibold text-slate-500 mb-2">
          {t("tradeJournalUi.screenshots")}
        </div>
        <div className="flex flex-wrap items-center gap-3">
          {images.map((img) => (
            <div key={img.id} className="relative group">
              <img
                data-testid={`${RESOURCES.journalImageThumb}-${img.id}`}
                src={`${API_BASE}/journal/images/${img.id}`}
                alt={img.filename || t("tradeJournalUi.tradeScreenshot")}
                className="h-20 w-20 object-cover rounded-lg border border-slate-300 bg-slate-100"
              />
              <button
                type="button"
                onClick={() => removeImage(img.id)}
                data-testid={`${RESOURCES.journalImageDelete}-${img.id}`}
                className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-slate-900 text-white opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center shadow-md"
                aria-label={t("tradeJournalUi.removeImage")}
              >
                <X size={12} />
              </button>
            </div>
          ))}
          <label
            className="h-20 w-20 rounded-lg border-2 border-dashed border-slate-300 flex flex-col items-center justify-center text-slate-500 hover:border-blue-500 hover:text-blue-800 cursor-pointer transition-colors"
          >
            <input
              ref={fileInputRef}
              type="file"
              accept="image/png,image/jpeg,image/webp,image/gif"
              className="hidden"
              data-testid={RESOURCES.journalImageUpload}
              onChange={(e) => e.target.files?.[0] && uploadImage(e.target.files[0])}
            />
            {uploading ? (
              <RefreshCw size={18} className="animate-spin" />
            ) : (
              <>
                <Camera size={18} />
                <span className="text-[10px] mt-1 font-semibold">{t("tradeJournalUi.add")}</span>
              </>
            )}
          </label>
        </div>
        <p className="text-[11px] text-slate-500 mt-2">
          {t("tradeJournalUi.imageFormats")}
        </p>
      </div>

      <div className="col-span-2 sm:col-span-4 flex justify-end gap-2">
        <Button type="button" variant="outline" onClick={onCancel} className="border-slate-300">
          {t("tradeJournalUi.close")}
        </Button>
        <Button
          type="submit"
          disabled={saving}
          data-testid={RESOURCES.journalSaveButton}
          className="bg-blue-800 hover:bg-blue-700 text-white"
        >
          {saving ? t("tradeJournalUi.saving") : isEdit ? t("tradeJournalUi.saveChanges") : t("tradeJournalUi.saveTrade")}
        </Button>
      </div>
    </form>
  );
}

/* -------------------------------------------------------------------------- */
/*  Expanded row (notes, updates, images)                                     */
/* -------------------------------------------------------------------------- */

function ExpandedRow({ row, onUpdate }) {
  const { t } = useTranslation();
  const [updateText, setUpdateText] = useState("");
  const [adding, setAdding] = useState(false);

  const addUpdate = async () => {
    const text = updateText.trim();
    if (!text) return;
    setAdding(true);
    try {
      const res = await api.post(`/journal/entries/${row.id}/updates`, { text });
      setUpdateText("");
      onUpdate(res.data);
      toast.success(t("tradeJournalUi.updateAdded"));
    } catch {
      toast.error(t("tradeJournalUi.couldNotAddUpdate"));
    } finally {
      setAdding(false);
    }
  };

  return (
    <div className="bg-slate-50 border-y border-slate-200 px-6 py-4 space-y-4">
      {(row.emotion_entry || row.emotion_management || row.emotion_exit) && (
        <div
          data-testid={`journal-emotion-summary-${row.id}`}
          className="grid grid-cols-3 gap-3"
        >
          {[
            { field: "emotion_entry", labelKey: "tradeJournalUi.emotionEntry" },
            { field: "emotion_management", labelKey: "tradeJournalUi.emotionManagement" },
            { field: "emotion_exit", labelKey: "tradeJournalUi.emotionExit" },
          ].map((p) => {
            const emo = row[p.field] ? EMOTIONS.find((e) => e.key === row[p.field]) : null;
            return (
              <div key={p.field} className="rounded-lg bg-white border border-slate-200 px-3 py-2">
                <div className="text-[10px] uppercase tracking-widest font-semibold text-slate-500">{t(p.labelKey)}</div>
                <div className="mt-0.5 text-sm font-semibold text-slate-800 flex items-center gap-1.5">
                  {emo ? <><span className="text-lg leading-none">{emo.emoji}</span> {emo.label}</> : <span className="text-slate-400 italic">{t("tradeJournalUi.emDash")}</span>}
                </div>
              </div>
            );
          })}
        </div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Notes */}
        <div>
          <div className="text-[10px] uppercase tracking-widest font-semibold text-slate-500 mb-1">
            {t("tradeJournalUi.originalNotes")}
          </div>
          <p className="text-sm text-slate-700 whitespace-pre-wrap min-h-[2rem]">
            {row.notes || <span className="text-slate-400 italic">{t("tradeJournalUi.noNotes")}</span>}
          </p>
        </div>

        {/* Images */}
        <div>
          <div className="text-[10px] uppercase tracking-widest font-semibold text-slate-500 mb-2">
            {t("tradeJournalUi.screenshots")}
          </div>
          {row.images && row.images.length > 0 ? (
            <div className="flex flex-wrap gap-2">
              {row.images.map((img) => (
                <a
                  key={img.id}
                  href={`${API_BASE}/journal/images/${img.id}`}
                  target="_blank"
                  rel="noreferrer"
                  className="block"
                >
                  <img
                    src={`${API_BASE}/journal/images/${img.id}`}
                    alt={img.filename || t("tradeJournalUi.screenshot")}
                    className="h-24 w-24 object-cover rounded-lg border border-slate-300 hover:border-blue-500 transition-colors"
                  />
                </a>
              ))}
            </div>
          ) : (
            <p className="text-xs text-slate-400 italic">{t("tradeJournalUi.noScreenshotsYet")}</p>
          )}
        </div>
      </div>

      {/* Updates timeline */}
      <div>
        <div className="text-[10px] uppercase tracking-widest font-semibold text-slate-500 mb-2">
          {t("tradeJournalUi.tradeProgress")}
        </div>
        <ol className="space-y-2 mb-3">
          {(row.updates || []).length === 0 && (
            <li className="text-xs text-slate-400 italic">{t("tradeJournalUi.noProgressNotes")}</li>
          )}
          {(row.updates || []).map((u, i) => (
            <li key={i} className="flex items-start gap-3 text-sm">
              <div className="w-1.5 h-1.5 rounded-full bg-blue-800 mt-2 shrink-0" />
              <div>
                <p className="text-slate-700 whitespace-pre-wrap">{u.text}</p>
                <p className="text-[10px] text-slate-400 mt-0.5">
                  {new Date(u.created_at).toLocaleString(undefined, {
                    month: "short", day: "numeric", hour: "2-digit", minute: "2-digit",
                  })}
                </p>
              </div>
            </li>
          ))}
        </ol>
        <div className="flex items-end gap-2">
          <div className="flex-1">
            <Textarea
              data-testid={`${RESOURCES.journalUpdateInput}-${row.id}`}
              value={updateText}
              onChange={(e) => setUpdateText(e.target.value)}
              placeholder={t("tradeJournalUi.tradeUpdatePlaceholder")}
              rows={2}
              className="bg-white"
            />
          </div>
          <Button
            type="button"
            disabled={adding || !updateText.trim()}
            onClick={addUpdate}
            data-testid={`${RESOURCES.journalUpdateAddButton}-${row.id}`}
            className="bg-slate-900 hover:bg-slate-800 text-white h-10"
          >
            <MessageSquarePlus size={14} className="mr-1.5" />
            {adding ? t("tradeJournalUi.adding") : t("tradeJournalUi.add")}
          </Button>
        </div>
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  Weekly-report modal                                                       */
/* -------------------------------------------------------------------------- */

function WeeklyReportDialog({ open, onOpenChange, onReportSaved }) {
  const { t, i18n } = useTranslation();
  const [streaming, setStreaming] = useState(false);
  const [content, setContent] = useState("");
  const [tradeCount, setTradeCount] = useState(null);
  const [reportId, setReportId] = useState(null);
  const [error, setError] = useState(null);
  const [days, setDays] = useState(7);
  const abortRef = useRef(null);

  const reset = () => {
    setContent("");
    setTradeCount(null);
    setReportId(null);
    setError(null);
  };

  const stream = async (rangeDays = days) => {
    reset();
    setStreaming(true);
    const controller = new AbortController();
    abortRef.current = controller;
    try {
      let resp = await fetch(`${API_BASE}/journal/weekly-report?days=${rangeDays}&lang=${(i18n.language || "en").slice(0, 2)}`, {
        method: "POST",
        credentials: "include",
        signal: controller.signal,
      });
      if (resp.status === 401) {
        try {
          await api.post("/auth/refresh");
          resp = await fetch(`${API_BASE}/journal/weekly-report?days=${rangeDays}&lang=${(i18n.language || "en").slice(0, 2)}`, {
            method: "POST",
            credentials: "include",
            signal: controller.signal,
          });
        } catch {
          throw new Error(t("tradeJournalUi.sessionExpired"));
        }
      }
      if (!resp.ok || !resp.body) throw new Error(`HTTP ${resp.status}`);
      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buf = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const parts = buf.split("\n\n");
        buf = parts.pop() || "";
        for (const part of parts) {
          const lines = part.split("\n");
          let event = "message";
          const dataParts = [];
          for (const line of lines) {
            if (line.startsWith("event: ")) event = line.slice(7).trim();
            else if (line.startsWith("data: ")) dataParts.push(line.slice(6));
          }
          const data = dataParts.join("\n");
          if (event === "report") setReportId(data.trim());
          else if (event === "meta") setTradeCount(parseInt(data, 10));
          else if (event === "error") setError(data);
          else if (event !== "done") {
            const decoded = data.replace(/\\n/g, "\n");
            setContent((prev) => prev + decoded);
          }
        }
      }
      onReportSaved?.();
    } catch (e) {
      if (e.name !== "AbortError") setError(e.message);
    } finally {
      setStreaming(false);
      abortRef.current = null;
    }
  };

  // Auto-start stream when the dialog opens
  // Auto-start stream when the dialog opens
  useEffect(() => {
    if (open && !streaming && !content && !error) {
      stream(days);
    }
  }, [open]);

  useEffect(() => {
    return () => abortRef.current?.abort();
  }, []);

  const download = async () => {
    if (!reportId) return;
    try {
      const resp = await fetch(`${API_BASE}/journal/weekly-reports/${reportId}/download`, {
        credentials: "include",
      });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const filename = resp.headers.get("Content-Disposition")?.match(/filename="([^"]+)"/)?.[1]
        || `summit-weekly-review.md`;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(t("tradeJournalUi.reportDownloaded"));
    } catch {
      toast.error(t("tradeJournalUi.downloadFailed"));
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid={RESOURCES.weeklyReportContent}
        className="max-w-2xl bg-slate-950 text-white border-slate-800"
      >
        <DialogHeader>
          <DialogTitle className="font-display text-2xl flex items-center gap-2 text-white">
            <Sparkles size={20} className="text-amber-400" />
            {t("tradeJournalUi.professorWeeklyReview")}
          </DialogTitle>
          <DialogDescription className="text-slate-400">
            {tradeCount !== null
              ? t("tradeJournalUi.analyzingTrades", { count: tradeCount, days })
              : t("tradeJournalUi.pullingTrades", { days })}
          </DialogDescription>
        </DialogHeader>

        {/* Date range selector */}
        <div className="flex flex-wrap items-center gap-2 -mt-1">
          <span className="text-[10px] uppercase tracking-widest font-semibold text-slate-500 mr-1">{t("tradeJournalUi.range")}</span>
          {[
            { d: 7,  labelKey: "tradeJournalUi.sevenDays" },
            { d: 14, labelKey: "tradeJournalUi.fourteenDays" },
            { d: 30, labelKey: "tradeJournalUi.thirtyDays" },
          ].map(({ d, labelKey }) => (
            <button
              key={d}
              type="button"
              disabled={streaming}
              data-testid={`resources-weekly-report-range-${d}`}
              onClick={() => { setDays(d); stream(d); }}
              className={`text-xs font-semibold px-3 py-1 rounded-full border transition-colors ${
                days === d
                  ? "bg-amber-500 text-slate-950 border-amber-500"
                  : "bg-transparent text-slate-300 border-slate-700 hover:bg-white/5"
              } disabled:opacity-50 disabled:cursor-not-allowed`}
            >
              {t(labelKey)}
            </button>
          ))}
        </div>

        <div className="min-h-[280px] max-h-[55vh] overflow-y-auto rounded-lg bg-white/5 border border-white/10 p-5 text-sm leading-relaxed whitespace-pre-wrap">
          {error ? (
            <p className="text-rose-300">{t("tradeJournalUi.warningEmoji")} {error}</p>
          ) : content ? (
            content
          ) : (
            <p className="text-slate-400 italic">
              {streaming ? t("tradeJournalUi.professorThinking") : t("tradeJournalUi.preparingReview")}
            </p>
          )}
          {streaming && content && (
            <span className="inline-block w-2 h-4 bg-amber-400 ml-1 animate-pulse align-middle" />
          )}
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3">
          <p className="text-[11px] text-slate-500 max-w-xs">
            {t("tradeJournalUi.reviewsSaved")}
          </p>
          <div className="flex items-center gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => stream(days)}
              disabled={streaming}
              data-testid={RESOURCES.weeklyReportRegenerate}
              className="bg-transparent border-slate-700 text-slate-200 hover:bg-white/5 hover:text-white"
            >
              <RefreshCw size={14} className={`mr-1.5 ${streaming ? "animate-spin" : ""}`} />
              {t("tradeJournalUi.regenerate")}
            </Button>
            <Button
              type="button"
              disabled={streaming || !reportId}
              onClick={download}
              data-testid={RESOURCES.weeklyReportDownload}
              className="bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold"
            >
              <Download size={14} className="mr-1.5" />
              {t("tradeJournalUi.downloadMd")}
            </Button>
            <Button
              type="button"
              disabled={streaming || !reportId}
              onClick={() => window.open(`/journal/export/${reportId}?auto=1`, "_blank")}
              data-testid="resources-weekly-report-export-pdf"
              className="bg-blue-800 hover:bg-blue-700 text-white font-semibold"
            >
              <Download size={14} className="mr-1.5" />
              {t("tradeJournalUi.exportPdf")}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/* -------------------------------------------------------------------------- */
/*  Past reports (below the log)                                              */
/* -------------------------------------------------------------------------- */

function PastReports({ refreshToken }) {
  const { t, i18n } = useTranslation();
  const [rows, setRows] = useState([]);

  useEffect(() => {
    api.get("/journal/weekly-reports")
      .then((r) => setRows(r.data))
      .catch(() => {});
  }, [refreshToken]);

  const download = async (id) => {
    try {
      const resp = await fetch(`${API_BASE}/journal/weekly-reports/${id}/download`, {
        credentials: "include",
      });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const filename = resp.headers.get("Content-Disposition")?.match(/filename="([^"]+)"/)?.[1]
        || "summit-weekly-review.md";
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast.error(t("tradeJournalUi.downloadFailed"));
    }
  };

  if (rows.length === 0) return null;
  const capped = rows.slice(0, 2); // backend also enforces this; belt-and-suspenders
  return (
    <div data-testid={RESOURCES.pastReportsSection} className="bg-white border border-slate-200 rounded-2xl p-6">
      <div className="flex items-baseline justify-between mb-4">
        <h3 className="font-display font-bold text-lg text-slate-900">{t("tradeJournalUi.recentReviews")}</h3>
        <span className="text-[10px] uppercase tracking-widest font-semibold text-slate-500">
          {t("tradeJournalUi.latestPrevious")}
        </span>
      </div>
      <ul className="divide-y divide-slate-100">
        {capped.map((r, i) => (
          <li
            key={r.id}
            data-testid={RESOURCES.pastReportRow}
            className="py-3 flex items-center justify-between gap-3"
          >
            <div className="flex items-center gap-3 min-w-0">
              <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${
                i === 0 ? "bg-amber-50 text-amber-700" : "bg-slate-100 text-slate-500"
              }`}>
                <FileText size={16} />
              </div>
              <div className="min-w-0">
                <p className="text-sm font-semibold text-slate-900 truncate flex items-center gap-2">
                  {i === 0 ? t("tradeJournalUi.latestReview") : t("tradeJournalUi.previousReview")}
                  <span className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold">
                    {t("tradeJournalUi.weekOf", { date: new Date(r.period_end).toLocaleDateString(undefined, { month: "short", day: "numeric" }) })}
                  </span>
                </p>
                <p className="text-xs text-slate-500">
                  {t("tradeJournalUi.tradeCountGenerated", { count: r.trade_count, date: new Date(r.generated_at).toLocaleDateString(i18n.language) })}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => download(r.id)}
                data-testid={`${RESOURCES.pastReportDownload}-${r.id}`}
                className="border-slate-300"
              >
                <Download size={13} className="mr-1.5" /> .md
              </Button>
              <Button
                type="button"
                size="sm"
                onClick={() => window.open(`/journal/export/${r.id}?auto=1`, "_blank")}
                data-testid={`resources-past-report-export-pdf-${r.id}`}
                className="bg-blue-800 hover:bg-blue-700 text-white"
              >
                <Download size={13} className="mr-1.5" /> PDF
              </Button>
            </div>
          </li>
        ))}
      </ul>
      <p className="mt-3 text-[11px] text-slate-500">
        {t("tradeJournalUi.onlyLatestPrevious")}
      </p>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  Root component                                                            */
/* -------------------------------------------------------------------------- */

export default function TradeJournal() {
  const { t } = useTranslation();
  const [entries, setEntries] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mode, setMode] = useState(null); // null | { type: "create" | "edit", row }
  const [expandedId, setExpandedId] = useState(null);
  const [reportOpen, setReportOpen] = useState(false);
  const [reportsRefresh, setReportsRefresh] = useState(0);
  const [accounts, setAccounts] = useState([]);
  const [plans, setPlans] = useState([]);

  const refresh = async () => {
    try {
      const [entriesRes, statsRes, accountsRes, plansRes] = await Promise.all([
        api.get("/journal/entries"),
        api.get("/journal/stats"),
        api.get("/accounts").catch(() => ({ data: [] })),
        api.get("/climb/plans").catch(() => ({ data: [] })),
      ]);
      setEntries(entriesRes.data);
      setStats(statsRes.data);
      setAccounts(accountsRes.data);
      setPlans(plansRes.data);
    } catch {
      toast.error(t("tradeJournalUi.couldNotLoadJournal"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); }, []);

  const remove = async (id) => {
    try {
      await api.delete(`/journal/entries/${id}`);
      await refresh();
    } catch {
      toast.error(t("tradeJournalUi.couldNotDelete"));
    }
  };

  const upsertLocal = (updated) => {
    setEntries((prev) => {
      const i = prev.findIndex((r) => r.id === updated.id);
      if (i === -1) return [updated, ...prev];
      const next = [...prev];
      next[i] = updated;
      return next;
    });
  };

  return (
    <div className="space-y-6">
      {/* Stats */}
      <div data-testid={RESOURCES.journalStats} className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <StatChip label="tradeJournalUi.totalTrades" value={stats?.total ?? t("tradeJournalUi.emDash")} />
        <StatChip label="tradeJournalUi.winRate" value={stats ? `${stats.win_rate}%` : t("tradeJournalUi.emDash")} tone="amber" />
        <StatChip
          label="tradeJournalUi.netPnl"
          value={stats ? `${stats.net_pnl >= 0 ? "+" : ""}$${Math.abs(stats.net_pnl).toLocaleString()}` : t("tradeJournalUi.emDash")}
          tone={stats && stats.net_pnl >= 0 ? "green" : "red"}
        />
        <StatChip label="tradeJournalUi.avgR" value={stats ? `${stats.avg_r}R` : t("tradeJournalUi.emDash")} tone="amber" />
      </div>

      {/* Trade log */}
      <div className="bg-white border border-slate-200 rounded-2xl p-6">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <div>
            <h3 className="font-display font-bold text-lg text-slate-900">{t("tradeJournalUi.tradeLog")}</h3>
            <p className="text-xs text-slate-500 mt-0.5">{t("tradeJournalUi.tradeLogHelp")}</p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => setReportOpen(true)}
              data-testid={RESOURCES.weeklyReportOpen}
              className="border-amber-400 text-amber-700 hover:bg-amber-50 hover:text-amber-800"
            >
              <Sparkles size={14} className="mr-1.5" />
              {t("tradeJournalUi.weeklyReview")}
            </Button>
            <Button
              type="button"
              onClick={() => setMode(mode ? null : { type: "create" })}
              data-testid={RESOURCES.journalAddButton}
              className="bg-blue-800 hover:bg-blue-700 text-white"
            >
              <Plus size={16} className="mr-1.5" />
              {mode ? t("tradeJournalUi.cancel") : t("tradeJournalUi.logTrade")}
            </Button>
          </div>
        </div>

        {mode && (
          <TradeForm
            initial={mode.type === "edit" ? mode.row : null}
            onCancel={() => { setMode(null); refresh(); }}
            onSaved={(updated) => { upsertLocal(updated); refresh(); }}
            accounts={accounts}
            plans={plans}
          />
        )}

        {loading ? (
          <div className="text-slate-500 text-sm py-10 text-center">{t("tradeJournalUi.loading")}</div>
        ) : entries.length === 0 ? (
          <div className="text-center py-10 bg-slate-50 rounded-xl border border-dashed border-slate-300">
            <BookOpen size={28} className="mx-auto text-slate-400 mb-3" />
            <p className="text-sm text-slate-600 font-semibold">{t("tradeJournalUi.noTradesYet")}</p>
            <p className="text-xs text-slate-500 mt-1">{t("tradeJournalUi.noTradesHelp")}</p>
          </div>
        ) : (
          <div className="overflow-x-auto -mx-6">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[10px] uppercase tracking-widest text-slate-500 border-b border-slate-200">
                  <th className="py-2 pl-6 font-semibold w-6"></th>
                  <th className="py-2 font-semibold">{t("tradeJournalUi.date")}</th>
                  <th className="py-2 font-semibold">{t("tradeJournalUi.instrument")}</th>
                  <th className="py-2 font-semibold">{t("tradeJournalUi.side")}</th>
                  <th className="py-2 font-semibold text-right">{t("tradeJournalUi.entry")}</th>
                  <th className="py-2 font-semibold text-right">{t("tradeJournalUi.exit")}</th>
                  <th className="py-2 font-semibold text-right">{t("tradeJournalUi.pnl")}</th>
                  <th className="py-2 font-semibold text-right">R</th>
                  <th className="py-2 font-semibold text-center">{t("tradeJournalUi.media")}</th>
                  <th className="py-2 pr-6 text-right w-24" />
                </tr>
              </thead>
              <tbody>
                {entries.map((r) => {
                  const positive = (r.pnl ?? 0) > 0;
                  const isOpen = expandedId === r.id;
                  const hasImages = (r.images || []).length > 0;
                  const hasUpdates = (r.updates || []).length > 0;
                  return (
                    <>
                      <tr
                        key={r.id}
                        data-testid={RESOURCES.journalRow}
                        className="border-b border-slate-100 hover:bg-slate-50 transition-colors cursor-pointer"
                        onClick={() => setExpandedId(isOpen ? null : r.id)}
                      >
                        <td className="py-3 pl-6">
                          <button
                            type="button"
                            data-testid={`${RESOURCES.journalExpandButton}-${r.id}`}
                            className={`text-slate-400 hover:text-slate-700 transition-transform ${isOpen ? "rotate-180" : ""}`}
                            aria-label={isOpen ? t("tradeJournalUi.collapse") : t("tradeJournalUi.expand")}
                          >
                            <ChevronDown size={15} />
                          </button>
                        </td>
                        <td className="py-3 text-slate-600 whitespace-nowrap">{r.traded_on}</td>
                        <td className="py-3 font-semibold text-slate-900">{r.instrument}</td>
                        <td className="py-3">
                          <span className={`inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded ${
                            r.direction === "long" ? "bg-emerald-50 text-emerald-700" : "bg-rose-50 text-rose-700"
                          }`}>
                            {r.direction === "long" ? <TrendingUp size={11} /> : <TrendingDown size={11} />}
                            {r.direction}
                          </span>
                        </td>
                        <td className="py-3 text-right font-mono text-slate-700">{r.entry ?? "—"}</td>
                        <td className="py-3 text-right font-mono text-slate-700">{r.exit ?? "—"}</td>
                        <td className={`py-3 text-right font-mono font-semibold ${
                          r.pnl == null ? "text-slate-400" : positive ? "text-emerald-600" : "text-rose-600"
                        }`}>
                          {r.pnl == null ? "—" : `${positive ? "+" : ""}$${r.pnl}`}
                        </td>
                        <td className="py-3 text-right font-mono text-slate-700">{r.r_multiple != null ? `${r.r_multiple}R` : "—"}</td>
                        <td className="py-3 text-center">
                          <span className="inline-flex items-center gap-1.5 text-xs text-slate-500">
                            <EmotionRowBadges entry={r} />
                            {hasImages && <Camera size={12} className="text-blue-800" />}
                            {hasUpdates && <MessageSquarePlus size={12} className="text-amber-600" />}
                            {!hasImages && !hasUpdates && !r.emotion_entry && !r.emotion_management && !r.emotion_exit && <span className="text-slate-300">—</span>}
                          </span>
                        </td>
                        <td className="py-3 pr-6 text-right">
                          <div className="inline-flex items-center gap-1">
                            <button
                              type="button"
                              onClick={(e) => { e.stopPropagation(); setMode({ type: "edit", row: r }); }}
                              data-testid={`${RESOURCES.journalEditButton}-${r.id}`}
                              className="w-7 h-7 rounded hover:bg-slate-100 text-slate-500 hover:text-blue-800 inline-flex items-center justify-center transition-colors"
                              aria-label={t("tradeJournalUi.edit")}
                            >
                              <Pencil size={13} />
                            </button>
                            <button
                              type="button"
                              onClick={(e) => { e.stopPropagation(); remove(r.id); }}
                              data-testid={`${RESOURCES.journalDeleteButton}-${r.id}`}
                              className="w-7 h-7 rounded hover:bg-rose-50 text-slate-400 hover:text-rose-600 inline-flex items-center justify-center transition-colors"
                              aria-label={t("tradeJournalUi.delete")}
                            >
                              <Trash2 size={13} />
                            </button>
                          </div>
                        </td>
                      </tr>
                      {isOpen && (
                        <tr>
                          <td colSpan={10} className="p-0">
                            <ExpandedRow row={r} onUpdate={upsertLocal} />
                          </td>
                        </tr>
                      )}
                    </>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <PastReports refreshToken={reportsRefresh} />

      <WeeklyReportDialog
        open={reportOpen}
        onOpenChange={(v) => { setReportOpen(v); if (!v) setReportsRefresh((x) => x + 1); }}
        onReportSaved={() => setReportsRefresh((x) => x + 1)}
      />
    </div>
  );
}
